import { useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "../_shared";
import { useApi, act, Pill, Panel, Btn, Field, inputCls, JsonEditor, Table, fmtTs, Reasons } from "./_kit";

export default function ControlCentre({ onlyTypes = null }) {
  const { data: types } = useApi("/control-centre/types");
  const { data: list, reload } = useApi("/control-centre/objects");
  const [selId, setSelId] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const items = (list?.items || []).filter((o) => !onlyTypes || onlyTypes.includes(o.config_type));
  const sel = items.find((o) => o.id === selId) || items[0];

  return (
    <div className="min-h-full">
      <PageHeader subtitle="Blackberrys Control Centre" title="Config Objects & Lifecycle"
        actions={<Btn data-testid="cc-new-draft-btn" onClick={() => setShowNew((v) => !v)}>New Draft</Btn>} />
      <div className="p-6 grid grid-cols-12 gap-4">
        <div className="col-span-12 lg:col-span-4 space-y-2">
          {showNew && <NewDraft types={(types?.types || []).filter((t) => !onlyTypes || onlyTypes.includes(t.type))} onDone={(o) => { setShowNew(false); reload(); setSelId(o.id); }} />}
          {items.map((o) => (
            <button key={o.id} data-testid={`cc-object-${o.config_type}`} onClick={() => setSelId(o.id)}
              className={`w-full text-left bg-white border rounded-sm px-4 py-3 transition-colors ${sel?.id === o.id ? "border-[#5A1A22]" : "border-black/10 hover:border-black/30"}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="text-sm font-semibold">{o.name}</div>
                <Pill value={o.state} />
              </div>
              <div className="text-[11px] text-neutral-500 mt-0.5">{o.config_type} · v{o.published?.version ?? "—"} · {fmtTs(o.updated_at)}</div>
            </button>
          ))}
        </div>
        <div className="col-span-12 lg:col-span-8">{sel && <ObjectDetail id={sel.id} onChange={reload} />}</div>
      </div>
    </div>
  );
}

function NewDraft({ types, onDone }) {
  const [t, setT] = useState(types[0]?.type || "");
  const [payload, setPayload] = useState({});
  const [note, setNote] = useState("");
  return (
    <Panel title="New draft" eyebrow="Draft → Preview → Test → Approve → Publish" testid="cc-new-draft-panel">
      <div className="space-y-3">
        <Field label="Config type">
          <select data-testid="cc-new-type" className={inputCls} value={t} onChange={(e) => setT(e.target.value)}>
            {types.map((x) => <option key={x.type} value={x.type}>{x.label}</option>)}
          </select>
        </Field>
        <Field label="Payload (JSON)"><JsonEditor value={payload} onChange={setPayload} rows={8} testid="cc-new-payload" /></Field>
        <Field label="Change note"><input data-testid="cc-new-note" className={inputCls} value={note} onChange={(e) => setNote(e.target.value)} /></Field>
        <Btn data-testid="cc-new-save" onClick={async () => onDone(await act(api.post("/control-centre/objects", { config_type: t, payload, change_note: note }), "Draft created"))}>Save Draft</Btn>
      </div>
    </Panel>
  );
}

function ObjectDetail({ id, onChange }) {
  const { data: o, reload } = useApi(`/control-centre/objects/${id}`);
  const [draft, setDraft] = useState(null);
  const [note, setNote] = useState("");
  const [effFrom, setEffFrom] = useState("");
  const [result, setResult] = useState(null);
  if (!o) return null;
  const refresh = () => { reload(); onChange(); };
  const run = (path, body = {}, msg) => async () => { setResult(await act(api.post(`/control-centre/objects/${id}/${path}`, body), msg)); refresh(); };
  const editing = draft ?? o.draft?.payload ?? o.published?.payload ?? {};
  const st = o.state;
  return (
    <div className="space-y-4">
      <Panel eyebrow={o.config_type} title={o.name} testid="cc-detail"
        right={<><Pill value={st} testid="cc-detail-state" /><span className="text-[11px] text-neutral-500">maker {o.maker || "—"} · checker {o.checker || "—"}</span></>}>
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-1">Effective (published v{o.published?.version ?? "—"})</div>
            <pre data-testid="cc-published-json" className="text-[11px] font-mono bg-[#F6F3EC] border border-black/10 rounded-sm p-3 max-h-80 overflow-auto">{JSON.stringify(o.published?.payload ?? {}, null, 2)}</pre>
            {o.published?.effective_from && <div className="text-[11px] mt-1">Effective from {fmtTs(o.published.effective_from)}</div>}
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-1">Draft</div>
            <JsonEditor value={editing} onChange={setDraft} rows={14} testid="cc-draft-editor" />
            <div className="flex flex-wrap gap-2 mt-2 items-end">
              <input data-testid="cc-change-note" placeholder="Change note" className={inputCls + " max-w-xs"} value={note} onChange={(e) => setNote(e.target.value)} />
              <Btn variant="ghost" data-testid="cc-save-draft" onClick={async () => { await act(api.put(`/control-centre/objects/${id}/draft`, { payload: draft ?? editing, change_note: note }), "Draft saved"); setDraft(null); refresh(); }}>Save draft</Btn>
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-black/10">
          <Btn variant="ghost" data-testid="cc-preview" disabled={!o.draft} onClick={run("preview", {}, "Preview computed")}>Preview diff</Btn>
          <Btn variant="ghost" data-testid="cc-test" disabled={!o.draft} onClick={run("test", { sample: {} }, "Dry-run complete")}>Test (dry-run)</Btn>
          <Btn data-testid="cc-submit" disabled={!o.draft || st === "pending_approval"} onClick={run("submit", { note }, "Submitted for approval")}>Submit (maker)</Btn>
          <Btn variant="gold" data-testid="cc-approve" disabled={st !== "pending_approval"} onClick={run("approve", { note }, "Approved")}>Approve (checker)</Btn>
          <Btn variant="danger" data-testid="cc-reject" disabled={st !== "pending_approval"} onClick={run("reject", { note }, "Rejected")}>Reject</Btn>
          <input data-testid="cc-effective-from" type="datetime-local" className={inputCls + " max-w-[200px]"} value={effFrom} onChange={(e) => setEffFrom(e.target.value)} />
          <Btn variant="accent" data-testid="cc-publish" disabled={!["approved", "scheduled"].includes(st)}
            onClick={run("publish", { effective_from: effFrom ? new Date(effFrom).toISOString() : null }, "Published")}>Publish</Btn>
        </div>
        {result?.changes && (
          <div className="mt-4" data-testid="cc-preview-result">
            <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-1">{result.change_count} change(s)</div>
            <Table cols={[{ key: "path", label: "Path" }, { key: "from", label: "From", render: (r) => JSON.stringify(r.from) }, { key: "to", label: "To", render: (r) => JSON.stringify(r.to) }]} rows={result.changes} rowKey="path" />
          </div>
        )}
        {result?.decision && (
          <div className="mt-4" data-testid="cc-test-result">
            <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-1">Dry-run decision (sample ₹6000 POS bill)</div>
            <Reasons reasons={result.decision.reasons} />
          </div>
        )}
      </Panel>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <Panel title="Version history" eyebrow="Rollback any version" testid="cc-versions">
          <Table rows={o.versions} cols={[
            { key: "version", label: "v" }, { key: "published_at", label: "Published", render: (r) => fmtTs(r.published_at) },
            { key: "published_by", label: "By" }, { key: "change_note", label: "Note" },
            { key: "a", label: "", render: (r) => r.version !== o.published?.version && <Btn variant="ghost" data-testid={`cc-rollback-${r.version}`} onClick={run(`rollback/${r.version}`, {}, `Rolled back to v${r.version}`)}>Rollback</Btn> },
          ]} />
        </Panel>
        <Panel title="Audit trail" eyebrow="Who did what" testid="cc-history">
          <Table rows={[...(o.history || [])].reverse()} rowKey="at" cols={[
            { key: "at", label: "When", render: (r) => fmtTs(r.at) }, { key: "by", label: "By" }, { key: "action", label: "Action", render: (r) => <Pill value={r.action} /> }, { key: "note", label: "Note" },
          ]} />
        </Panel>
      </div>
    </div>
  );
}
